import { format } from 'date-fns';
import { RenderedSharesResponse } from './Analytics';
import { UserTraffic } from './Traffic';

export type RenderedSharesType = {
	exhibitId: string;
	shares: UserTraffic[];
	sessionLengths?: number[];
};

export type GraphDataPoint = {
	date: string;
	value: number;
	timestamp: number;
};

export const aggregateRenderedShares = (rawData: RenderedSharesResponse[]): RenderedSharesType[] => {
	// Create a Map to store aggregated data by exhibit and date
	const exhibitDataMap = new Map<string, Map<string, GraphDataPoint>>();

	// Process raw data in a single pass
	for (const share of rawData) {
		const { exhibitId, dateRendered } = share.metadata;
		const date = new Date(dateRendered);
		const dateKey = format(date, 'yyyy-MM-dd');
		const displayDate = format(date, 'MMM d, yyyy');
		const timestamp = date.getTime();

		// Get or create the date map for this exhibit
		let dateMap = exhibitDataMap.get(exhibitId);
		if (!dateMap) {
			dateMap = new Map();
			exhibitDataMap.set(exhibitId, dateMap);
		}

		// Update or create the data point
		const existing = dateMap.get(dateKey);
		if (existing) {
			existing.value += 1;
		} else {
			dateMap.set(dateKey, {
				date: displayDate,
				value: 1,
				timestamp,
			});
		}
	}

	// Convert map to final array format in a single pass
	return Array.from(exhibitDataMap.entries()).map(([exhibitId, dateMap]) => {
		const shares = Array.from(dateMap.values())
			.sort((a, b) => a.timestamp - b.timestamp)
			.map((point) => ({
				key: point.date,
				value: point.value,
				display: point.date,
				timestamp: point.timestamp,
			}));

		return {
			exhibitId,
			shares,
		};
	});
};

export interface BotRenderedShareMetadata {
	exhibitId: string;
	areaId: string;
	organizationId: string;
	locationId: string;
	requestType: string;
	userAgent: string;
	dateRendered: string;
}

export interface BotRenderedShare {
	_id: { $oid: string };
	type: string;
	udid: string;
	os: string;
	metadata: BotRenderedShareMetadata;
	ip: string;
	createdDate: { $date: { $numberLong: string } };
	modifiedDate: { $date: { $numberLong: string } };
	status: number;
}
